/**
 * Platform Billing Hook
 *
 * Manages Forge platform subscription lifecycle:
 * - Query current subscription status
 * - Create checkout session for new subscriptions
 * - Open customer portal for self-service management
 * - Query billing event history
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { invokeEdgeFunction } from "@/lib/edge-functions";
import { getEdgeFunctionErrorMessage } from "@/lib/edge-function-errors";
import { useOrganization } from "@/hooks/useOrganization";
import type { SubscriptionTier } from "@/lib/tier-config";
import { toast } from "sonner";

// ── Types ────────────────────────────────────────────────────

export interface PlatformSubscription {
  id: string;
  organization_id: string;
  stripe_customer_id: string;
  stripe_subscription_id: string;
  stripe_price_id: string;
  plan: "plant" | "harvest" | "flourish";
  status: "active" | "trialing" | "past_due" | "canceled" | "paused" | "incomplete";
  billing_interval: "month" | "year";
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at: string | null;
  canceled_at: string | null;
  trial_end: string | null;
  created_at: string;
  updated_at: string;
}

export interface PlatformBillingEvent {
  id: string;
  organization_id: string | null;
  event_id: string;
  event_type: string;
  status: string;
  processing_error: string | null;
  processed_at: string | null;
  created_at: string;
}

export interface ForgeProductEnrollment {
  id: string;
  organization_id: string;
  product: "forge";
  plan: string;
  status: "trial" | "active" | "suspended" | "cancelled";
  settings: Record<string, unknown>;
  activated_at: string;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
}

// ── Subscription Status ──────────────────────────────────────

export function usePlatformSubscription() {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["platform-subscription", organizationId],
    queryFn: async () => {
      if (!organizationId) return null;

      const { data, error } = await supabase
        .from("platform_subscriptions")
        .select("*")
        .eq("organization_id", organizationId)
        .maybeSingle();

      if (error) throw error;
      return data as PlatformSubscription | null;
    },
    enabled: !!organizationId,
    staleTime: 30_000,
  });
}

export function useForgeEntitlement() {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ["forge-entitlement", organizationId],
    queryFn: async () => {
      if (!organizationId) return null;

      const { data, error } = await supabase
        .from("product_enrollments")
        .select("*")
        .eq("organization_id", organizationId)
        .eq("product", "forge")
        .maybeSingle();

      if (error) throw error;
      return data as ForgeProductEnrollment | null;
    },
    enabled: !!organizationId,
    staleTime: 30_000,
  });
}

/**
 * Org-level Stripe customer id — the billing-portal edge function resolves
 * this FIRST, falling back to platform_subscriptions.stripe_customer_id only
 * if it's empty. UI billing-account checks must consider both paths, or an
 * org with an org-level customer but no platform_subscriptions row looks
 * like it has no billing account when the edge function can serve it fine.
 */
export function useOrganizationStripeCustomer() {
  const { organization } = useOrganization();

  return useQuery({
    queryKey: ["organization-stripe-customer", organization?.id],
    queryFn: async () => {
      if (!organization?.id) return null;

      const { data, error } = await supabase
        .from("organizations")
        .select("stripe_customer_id")
        .eq("id", organization.id)
        .maybeSingle();

      if (error) throw error;
      return (data?.stripe_customer_id as string | null) ?? null;
    },
    enabled: !!organization?.id,
    staleTime: 30_000,
  });
}

// ── Create Checkout Session ──────────────────────────────────

export function useCreateBillingCheckout() {
  const { organization } = useOrganization();
  const queryClient = useQueryClient();

  return useMutation({
    // The client sends a tier, never a raw Stripe Price ID — the edge
    // function resolves the Price ID server-side so this authenticated
    // org-admin endpoint can't be used to buy an arbitrary Stripe Price.
    mutationFn: async (params: { tier: Exclude<SubscriptionTier, "free">; success_url?: string; cancel_url?: string }) => {
      if (!organization?.id) throw new Error("No organization selected");

      const response = await invokeEdgeFunction("stripe-billing-create-checkout" as any, {
        method: "POST",
        body: {
          organization_id: organization.id,
          tier: params.tier,
          success_url: params.success_url,
          cancel_url: params.cancel_url,
        },
      });

      if (response.error) {
        throw new Error(
          getEdgeFunctionErrorMessage(response.error, "Failed to create checkout session")
        );
      }

      return response.data as { session_id: string; session_url: string };
    },
    onSuccess: (data) => {
      // Redirect to Stripe Checkout
      if (data.session_url) {
        window.location.href = data.session_url;
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to start checkout");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["platform-subscription"] });
    },
  });
}

// ── Customer Portal ──────────────────────────────────────────

export function useCustomerPortal() {
  const { organization } = useOrganization();

  return useMutation({
    mutationFn: async (params?: { return_url?: string }) => {
      if (!organization?.id) throw new Error("No organization selected");

      const response = await invokeEdgeFunction("stripe-billing-customer-portal" as any, {
        method: "POST",
        body: {
          organization_id: organization.id,
          return_url: params?.return_url,
        },
      });

      if (response.error) {
        throw new Error(
          getEdgeFunctionErrorMessage(response.error, "Failed to open billing portal")
        );
      }

      return response.data as { portal_url: string };
    },
    onSuccess: (data) => {
      if (data.portal_url) {
        window.location.href = data.portal_url;
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to open billing portal");
    },
  });
}

// ── Billing Events History ───────────────────────────────────

export function useBillingEvents(limit = 20) {
  const { organization } = useOrganization();

  return useQuery({
    queryKey: ["platform-billing-events", organization?.id, limit],
    queryFn: async () => {
      if (!organization?.id) return [];

      const { data, error } = await supabase
        .from("platform_billing_events")
        .select("id, organization_id, event_id, event_type, status, processing_error, processed_at, created_at")
        .eq("organization_id", organization.id)
        .order("created_at", { ascending: false })
        .limit(limit);

      if (error) throw error;
      return (data || []) as PlatformBillingEvent[];
    },
    enabled: !!organization?.id,
    staleTime: 60_000,
  });
}

// ── Billing Status Helpers ───────────────────────────────────

const VALID_TIERS = new Set<SubscriptionTier>(["free", "plant", "harvest", "flourish"]);

function normalizeTier(plan: string | null | undefined): SubscriptionTier {
  return VALID_TIERS.has(plan as SubscriptionTier) ? (plan as SubscriptionTier) : "free";
}

/**
 * Derive feature access from Forge product enrollment, not Stripe billing.
 *
 * Stripe rows describe money movement. Product enrollments describe what Forge
 * should unlock. Missing/unknown/cancelled/suspended enrollment rows fail closed
 * to "free"; explicit trial and active enrollments honor their configured plan.
 */
export function deriveEffectiveTier(
  entitlement: Pick<ForgeProductEnrollment, "status" | "plan"> | null | undefined,
): SubscriptionTier {
  if (!entitlement) return "free";
  if (entitlement.status !== "trial" && entitlement.status !== "active") return "free";
  return normalizeTier(entitlement.plan);
}

export function useBillingStatus() {
  const {
    data: subscription,
    isLoading: subscriptionLoading,
    error: subscriptionError,
  } = usePlatformSubscription();
  const {
    data: entitlement,
    isLoading: entitlementLoading,
    error: entitlementError,
  } = useForgeEntitlement();

  const isActive = subscription?.status === "active" || subscription?.status === "trialing";
  const isPastDue = subscription?.status === "past_due";
  const isCanceled = subscription?.status === "canceled";
  const isCanceling = !!subscription?.cancel_at && !isCanceled;

  // Trial is an explicit Forge product enrollment status. Stripe subscription
  // rows remain billing state only and are not used for feature gating.
  const isTrial = entitlement?.status === "trial";
  const plan = normalizeTier(entitlement?.plan);

  // effectiveTier: used by tier-gating hooks. See deriveEffectiveTier (fail-closed).
  const effectiveTier = deriveEffectiveTier(entitlement);

  const nextBillingDate = subscription?.current_period_end
    ? new Date(subscription.current_period_end)
    : null;

  const cancelDate = subscription?.cancel_at
    ? new Date(subscription.cancel_at)
    : null;

  return {
    subscription,
    entitlement,
    isLoading: subscriptionLoading || entitlementLoading,
    error: subscriptionError || entitlementError,
    isActive,
    isPastDue,
    isCanceled,
    isCanceling,
    isTrial,
    nextBillingDate,
    cancelDate,
    plan,
    effectiveTier,
    entitlementStatus: entitlement?.status ?? null,
    billingInterval: subscription?.billing_interval || null,
  };
}
